import type * as DB from 'interfaces/database';
import type * as PI from 'interfaces/project';
import defaults from 'store/modules/productstate/defaults';
import { normalizeTemplatePositionTextAttributes } from 'tools/template-position-text-attributes';
import _ from 'underscore';
import {
	getTextObjectFontSize,
	isTextObjectFontSizeSource,
} from 'utils/font';

interface HasReplaceableProductContentOptions {
	objectModels: PI.PageObjectModel[];
	themePageStateModels: PI.PageStateModel[];
}

const THEME_TEXT_COMPARISON_IGNORED_PROPERTIES: ReadonlySet<string> = new Set([
	'attributes',
	'fontsize',
	'id',
	'objectid',
	'pageid',
	'pointsize',
	'positionAttributes',
	'productid',
	'text_formatted',
	'text_formatted_for_canvas',
	'text_svg',
	'version',
]);

/**
 * Checks whether a project text object still matches the authored theme object
 * it was copied from. Generated storage and rendering fields are ignored, while
 * font size and inherited position styles are compared in their normalized form.
 *
 * @param objectModel Current project {@link PI.PageObjectModel} to inspect.
 * @param themeTextObjectsById Loaded theme text objects keyed by their source IDs.
 * @returns Whether the object is an unchanged copy of a loaded theme text object.
 */
function isUnchangedThemeTextObject(
	objectModel: PI.PageObjectModel,
	themeTextObjectsById: ReadonlyMap<string, PI.PageObjectModel>,
): boolean {
	if (
		objectModel.type !== 'text'
		|| typeof objectModel.objectid === 'undefined'
		|| objectModel.objectid === null
	) {
		return false;
	}

	const themeObjectModel = themeTextObjectsById.get(String(objectModel.objectid));

	if (!themeObjectModel) {
		return false;
	}

	const currentObjectRecord = objectModel as unknown as Record<string, unknown>;
	const themeObjectRecord = themeObjectModel as unknown as Record<string, unknown>;
	const expectedObjectRecord: Record<string, unknown> = {
		...(defaults.objectModel as unknown as Record<string, unknown>),
		...themeObjectRecord,
	};
	let themeAttributes = themeObjectRecord.attributes;

	if (typeof themeAttributes === 'string') {
		try {
			themeAttributes = JSON.parse(themeAttributes);
		} catch {
			themeAttributes = null;
		}
	}

	const normalizedThemeAttributes = normalizeTemplatePositionTextAttributes(
		themeAttributes as DB.TemplatePositionTextAttributes | null | undefined,
	);
	const themePositionAttributes = (
		themeObjectModel.positionAttributes
		?? normalizedThemeAttributes.styles
		?? null
	);

	if (
		!_.isEqual(
			objectModel.positionAttributes ?? null,
			themePositionAttributes,
		)
	) {
		return false;
	}

	if (isTextObjectFontSizeSource(themeObjectModel)) {
		if (
			!isTextObjectFontSizeSource(objectModel)
			|| getTextObjectFontSize(objectModel) !== getTextObjectFontSize(themeObjectModel)
		) {
			return false;
		}
	} else if (isTextObjectFontSizeSource(objectModel)) {
		return false;
	}

	if (expectedObjectRecord.effect === 'noeffect') {
		expectedObjectRecord.effect = null;
	}

	if (expectedObjectRecord.mask === 'square') {
		expectedObjectRecord.mask = null;
	}

	const comparisonProperties = new Set([
		...Object.keys(expectedObjectRecord),
		...Object.keys(currentObjectRecord),
	]);

	return Array.from(comparisonProperties).every((property) => (
		property.startsWith('_')
		|| THEME_TEXT_COMPARISON_IGNORED_PROPERTIES.has(property)
		|| _.isEqual(
			currentObjectRecord[property],
			expectedObjectRecord[property],
		)
	));
}

/**
 * Determines whether autofill would replace meaningful existing content.
 * Decorative objects and unchanged text copied from the active theme are not
 * considered user content; edited theme text and other editable objects are.
 *
 * @param options Current product objects and loaded theme page states.
 * @param options.objectModels Current project {@link PI.PageObjectModel} entries.
 * @param options.themePageStateModels Loaded theme {@link PI.PageStateModel} entries.
 * @returns Whether the replacement confirmation dialog should be shown.
 */
export function hasReplaceableProductContent(options: HasReplaceableProductContentOptions): boolean {
	const themeTextObjectsById: ReadonlyMap<string, PI.PageObjectModel> = new Map(
		options.themePageStateModels
			.flatMap((pageStateModel) => pageStateModel.objects)
			.filter((objectModel) => objectModel.type === 'text')
			.map((objectModel) => [
				String(objectModel.id),
				objectModel,
			] as const),
	);

	return options.objectModels.some((objectModel) => (
		objectModel.editable === 1
		&& !objectModel.decorative
		&& !isUnchangedThemeTextObject(
			objectModel,
			themeTextObjectsById,
		)
	));
}
